import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaRegImage } from "react-icons/fa6";
import { IoCamera } from "react-icons/io5";
import { LuGalleryVerticalEnd } from "react-icons/lu";
import ReactModal from "react-modal";
import Webcam from "react-webcam";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { FaStop } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";

function getCroppedImg(image, crop) {
  const canvas = document.createElement("canvas");
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  canvas.width = crop.width;
  canvas.height = crop.height;
  const ctx = canvas.getContext("2d");

  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    crop.width,
    crop.height,
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Canvas is empty"));
          return;
        }
        resolve(blob);
      },
      "image/jpeg",
      1,
    );
  });
}

const videoConstraints = {
  width: 1200,
  height: 800,
  facingMode: "user",
};

const UploadImageButton = ({ handleImageUpload, handleImageCapture }) => {
  const inputRef = useRef(null);

  const [isActive, setIsActive] = useState(false);
  const [cameraModalOpen, setCameraModalOpen] = useState(false);
  const [showCrop, setShowCrop] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [crop, setCrop] = useState({
    unit: "%",
    width: 100,
    height: 100,
    x: 0,
    y: 0,
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [imageRef, setImageRef] = useState(null);
  const [imageCaption, setImageCaption] = useState("");
  const containerRef = useRef();
  const imageInputRef = useRef();
  const webcamRef = useRef();

  // Handle outside click for dropdown
  useEffect(() => {
    const eventHandler = (e) => {
      if (containerRef?.current && !containerRef?.current?.contains(e.target)) {
        setIsActive(false);
      }
    };
    document.addEventListener("mouseup", eventHandler);

    return () => document.removeEventListener("mouseup", eventHandler);
  }, []);

  // --- IMAGE CAPTURE & CROP ---
  const capture = useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();
    setCropImage(imageSrc);
    setShowCrop(true);
    setCrop({ unit: "%", width: 100, height: 100, x: 0, y: 0 });
    setCompletedCrop(null);
    setImageCaption("");
  }, [webcamRef]);

  // Save cropped or original image
  const handleCropSave = async () => {
    if (imageRef) {
      let file;
      if (!completedCrop || !completedCrop.width || !completedCrop.height) {
        // No crop: send original
        const res = await fetch(cropImage);
        const blob = await res.blob();
        file = new File([blob], "image.jpg", { type: blob.type });
      } else {
        // Cropped
        const croppedBlob = await getCroppedImg(imageRef, completedCrop);
        file = new File([croppedBlob], "cropped-image.jpg", {
          type: "image/jpeg",
        });
      }
      handleImageCapture(file, imageCaption);
    }
    setShowCrop(false);
    setCropImage(null);
    setCompletedCrop(null);
    setImageRef(null);
    setImageCaption("");
    setCameraModalOpen(false);
  };

  const handleCropCancel = () => {
    setShowCrop(false);
    setCropImage(null);
    setCompletedCrop(null);
    setImageRef(null);
    setImageCaption("");
  };

  // File picker (from gallery)
  const handleFileChange = async (e) => {
    const { files } = e.target;
    if (files && files[0]) {
      const file = files[0];
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = () => {
          setCropImage(reader.result);
          setShowCrop(true);
          setCrop({ unit: "%", width: 100, height: 100, x: 0, y: 0 });
          setCompletedCrop(null);
          setImageCaption("");
        };
        reader.readAsDataURL(file);
      }
    }
  };

  return (
    <>
      <div className="relative">
        <button
          className="rounded-xl border border-white/30 px-4 py-2 text-white"
          onClick={() => setIsActive(true)}
        >
          <FaRegImage className="text-xl" />
        </button>
        <input
          ref={imageInputRef}
          className="hidden"
          multiple={true}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
        />
        <ul
          className={`${
            isActive ? "flex flex-col gap-1" : "hidden"
          } absolute -top-2 z-50 -translate-y-full text-xs transition-all duration-300`}
          ref={containerRef}
          onClick={() => setIsActive(false)}
        >
          <li
            onClick={() => imageInputRef.current.click()}
            className="flex w-fit cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-yellowish px-2.5 py-2 text-blackishV2"
          >
            <LuGalleryVerticalEnd className="text-base" />
            Select from Files
          </li>
          <li
            onClick={() => setCameraModalOpen(true)}
            className="flex w-fit cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-yellowish px-2.5 py-2 text-blackishV2"
          >
            <IoCamera className="text-base" />
            Open Camera
          </li>
        </ul>
      </div>

      {/* Camera Modal */}
      <ReactModal
        isOpen={cameraModalOpen}
        onRequestClose={() => setCameraModalOpen(false)}
        className="flex h-screen w-screen items-center justify-center bg-black bg-opacity-80"
        overlayClassName="fixed inset-0 bg-black bg-opacity-80 z-50"
        ariaHideApp={false}
      >
        <div className="relative flex flex-col items-center justify-center rounded-xl bg-black p-6">
          <div
            className="absolute right-8 top-8 z-50 aspect-square cursor-pointer rounded-full bg-black bg-opacity-60 p-2 text-2xl text-white"
            onClick={() => {
              setCameraModalOpen(false);
            }}
          >
            <IoMdClose />
          </div>
          {showCrop ? (
            // Crop UI
            <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black bg-opacity-80">
              <div className="relative flex h-[60vw] w-[90vw] flex-col items-center justify-center rounded-xl bg-black">
                <ReactCrop
                  crop={crop}
                  onChange={(_, percentCrop) => setCrop(percentCrop)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={undefined}
                  minWidth={10}
                  minHeight={10}
                >
                  <img
                    src={cropImage}
                    alt="Crop"
                    style={{ maxWidth: "100%", maxHeight: "60vh" }}
                    onLoad={(e) => setImageRef(e.currentTarget)}
                  />
                </ReactCrop>
                <div className="mt-4 flex w-full flex-col items-center gap-2">
                  <input
                    type="text"
                    value={imageCaption}
                    onChange={(e) => setImageCaption(e.target.value)}
                    placeholder="Add a caption (optional)"
                    className="border-greyishV3 w-[47.5%] rounded-md border bg-black px-3 py-2 text-white"
                  />
                  <div className="z-50 mt-2 flex gap-4">
                    <button
                      className="rounded bg-red-500 px-4 py-2 text-white"
                      onClick={handleCropCancel}
                    >
                      Cancel
                    </button>
                    <button
                      className="rounded bg-green-500 px-4 py-2 text-white"
                      onClick={handleCropSave}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              <Webcam
                style={{
                  height: "93%",
                  width: "100%",
                  borderRadius: "",
                }}
                audio={false}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                videoConstraints={videoConstraints}
                mirrored={false}
              />
              <button
                className="absolute bottom-10 left-1/2 -translate-x-1/2 rounded-full border-4 border-red-500 bg-red-800 bg-opacity-60 p-6 text-red-200"
                onClick={capture}
              >
                <FaStop size={20} className="invisible" />
              </button>
            </>
          )}
        </div>
      </ReactModal>

      {/* Crop Modal */}
      {showCrop && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black bg-opacity-80">
          <div className="relative flex h-[60vw] w-[90vw] flex-col items-center justify-center rounded-xl bg-black">
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={undefined}
              minWidth={10}
              minHeight={10}
            >
              <img
                src={cropImage}
                alt="Crop"
                style={{ maxWidth: "100%", maxHeight: "60vh" }}
                onLoad={(e) => setImageRef(e.currentTarget)}
              />
            </ReactCrop>
            <div className="mt-4 flex w-full flex-col items-center gap-2">
              <input
                type="text"
                value={imageCaption}
                onChange={(e) => setImageCaption(e.target.value)}
                placeholder="Add a caption (optional)"
                className="border-greyishV3 w-[47.5%] rounded-md border bg-black px-3 py-2 text-white"
              />
              <div className="z-50 mt-2 flex gap-4">
                <button
                  className="rounded bg-red-500 px-4 py-2 text-white"
                  onClick={handleCropCancel}
                >
                  Cancel
                </button>
                <button
                  className="rounded bg-green-500 px-4 py-2 text-white"
                  onClick={handleCropSave}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UploadImageButton;
